<template>
  <!-- Legacy Mission and Vision layout (views/about/Sections/MissionAndVisionSection.vue) with the
       new copy: mission on the left, the DIGU circle with its turning ring in the middle, vision
       on the right, joined by one line (vertical on phones). Each title sits above the line and
       its statement below it. -->
  <sec-section :divider="false" :aria-label="mv.label" class="about-mv">
    <div ref="root" class="about-mv__row">
      <span class="about-mv__line" aria-hidden="true"></span>

      <div class="about-mv__text about-mv__text--mission">
        <h2 class="o700 about-mv__title">{{ mv.mission.eyebrow }}</h2>
        <p class="m500 about-mv__desc">{{ mv.mission.text }}</p>
      </div>

      <div class="about-mv__circle" aria-hidden="true">
        <span class="about-mv__ring"></span>
        <span class="about-mv__disc">
          <img class="about-mv__img about-mv__img--wide" :src="imgWide" alt="" width="501" height="491" />
          <img class="about-mv__img about-mv__img--narrow" :src="imgNarrow" alt="" width="501" height="491" />
        </span>
      </div>

      <div class="about-mv__text about-mv__text--vision">
        <h2 class="o700 about-mv__title">{{ mv.vision.eyebrow }}</h2>
        <p class="m500 about-mv__desc">{{ mv.vision.text }}</p>
      </div>
    </div>
  </sec-section>
</template>

<script setup>
// Motion (GSAP via useMotion): the ring turns slowly and continuously; when the row scrolls in,
// the line draws out from the circle, the circle pops in, and mission and vision glide in from
// their sides (from above and below on phones). Reduced motion: everything at rest, no turning.
import { ref } from 'vue';
import { gsap } from '@/plugins/gsap';
import { useMotion } from '@/composables/security/useMotion';
import SecSection from '@/components/security/ui/SecSection.vue';
import { ABOUT } from '@/content/security/about';
// Same centre artwork as the legacy section (imported, unchanged).
import imgWide from '@/assets/img/missionAndVision.svg';
import imgNarrow from '@/assets/img/missionAndVision-mobile.svg';

const mv = ABOUT.missionVision;
const root = ref(null);

useMotion(root, (el) => {
  const stacked = window.matchMedia('(max-width: 1012px)').matches;
  const line = el.querySelector('.about-mv__line');
  const circle = el.querySelector('.about-mv__circle');
  const mission = el.querySelector('.about-mv__text--mission');
  const vision = el.querySelector('.about-mv__text--vision');

  gsap.to(el.querySelector('.about-mv__ring'), { rotation: 360, duration: 9, ease: 'none', repeat: -1 });

  gsap
    .timeline({ scrollTrigger: { trigger: el, start: 'top 78%', once: true } })
    .from(line, { [stacked ? 'scaleY' : 'scaleX']: 0, duration: 1.1, ease: 'expo.out' })
    .from(circle, { scale: 0.6, autoAlpha: 0, duration: 0.9, ease: 'back.out(1.6)' }, '<0.1')
    .from(mission, { [stacked ? 'y' : 'x']: stacked ? -24 : -48, autoAlpha: 0, duration: 0.9, ease: 'expo.out' }, '<0.25')
    .from(vision, { [stacked ? 'y' : 'x']: stacked ? 24 : 48, autoAlpha: 0, duration: 0.9, ease: 'expo.out' }, '<0.1');
});
</script>

<style scoped>
.about-mv {
  background: var(--sec-surface);
}

/* Phones and tablets: stacked, joined by a vertical line. */
.about-mv__row {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sec-s4);
  text-align: center;
}

.about-mv__line {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background: var(--sec-accent);
  border-radius: 2px;
}

.about-mv__text {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--sec-s2);
  max-width: 460px;
  padding: var(--sec-s2) var(--sec-s3);
  background: var(--sec-surface);
}

.about-mv__title {
  margin: 0;
  font-size: 1.75rem;
  line-height: 1.2;
  color: var(--sec-ink);
}

.about-mv__desc {
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--sec-ink-2);
  text-wrap: balance;
}

.about-mv__circle {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 220px;
  height: 220px;
}

.about-mv__disc {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 50%;
  background: var(--sec-surface);
  border: 8px solid var(--sec-surface);
  box-shadow: 0 0 0 1px var(--sec-line), 0 24px 48px -24px var(--sec-header-shadow);
}

.about-mv__ring {
  position: absolute;
  inset: -10px;
  border-radius: 50%;
  border: 6px solid transparent;
  border-top-color: var(--sec-accent);
  border-bottom-color: var(--sec-accent-strong);
}

.about-mv__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.about-mv__img--wide {
  display: none;
}

/* Desktop: mission | circle | vision on one row, joined by a horizontal line. Each title sits
   above the line and its statement below it. */
@media only screen and (min-width: 1013px) {
  .about-mv__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: stretch;
    gap: 3rem;
  }
  .about-mv__line {
    top: 50%;
    bottom: auto;
    left: 0;
    right: 0;
    width: auto;
    height: 2px;
    transform: translateY(-50%);
  }
  .about-mv__text {
    display: grid;
    grid-template-rows: 1fr 3.5rem 1fr;
    gap: 0;
    max-width: none;
    padding: 0;
    background: none;
  }
  .about-mv__title {
    grid-row: 1;
    align-self: end;
  }
  .about-mv__desc {
    grid-row: 3;
    align-self: start;
  }
  .about-mv__text--mission {
    text-align: right;
  }
  .about-mv__text--vision {
    text-align: left;
  }
  .about-mv__title,
  .about-mv__desc {
    padding-inline: 1rem;
    background: var(--sec-surface);
  }
  .about-mv__text--mission .about-mv__title,
  .about-mv__text--mission .about-mv__desc {
    justify-self: end;
  }
  .about-mv__text--vision .about-mv__title,
  .about-mv__text--vision .about-mv__desc {
    justify-self: start;
  }
  .about-mv__circle {
    width: 300px;
    height: 300px;
  }
  .about-mv__img--wide {
    display: block;
  }
  .about-mv__img--narrow {
    display: none;
  }
}
</style>
